<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
  >
    <template v-slot:header>
      <div>
        <v-card-title class="screen-title"> ポリシー編集 </v-card-title>
        <v-card-subtitle class="screen-sub-title">
          イントラネットFW プラットフォームゲートウェイ {{ srcZone }} →
          {{ srcZone === "WVS2" ? "KCPS" : "WVS2" }}
        </v-card-subtitle>
      </div>
      <button
        type="button"
        aria-label="Close"
        class="close"
        @click="closeModal"
      >
        ×
      </button>
    </template>
    <template v-if="isLoaded" v-slot:body>
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container fluid>
            <v-row
              no-gutters
              justify="space-between"
              class="my-2"
              align="center"
            >
              <span>{{ intranetPolicyTable.items.length }}件を表示</span>
              <v-btn
                color="primary"
                @click="showIntraPolicyRegister"
                :disabled="limitPolicy"
                >ルール追加
              </v-btn>
            </v-row>
            <app-draggable-table
              density="compact"
              :fields="intranetPolicyTable.fields"
              :items="intranetPolicyTable.items"
              primary-key="policyId"
              :hover="true"
              sticky-header="500px"
              :td-class="tdClass"
              :tr-class="policyRowClass"
              @click:row="showIntraPolicyModify"
            >
              <!-- 送信元 -->
              <template #cell(srcAddress)="{ item }">
                <intranet-policy-src-address
                  :ip-address-list="item.srcAddressList"
                />
              </template>

              <!-- 宛先 -->
              <template #cell(dstAddress)="{ item }">
                <intranet-policy-dst-address
                  :ip-address-list="item.dstAddressList"
                />
              </template>

              <!-- サービス -->
              <template #cell(service)="{ item }">
                <intranet-policy-service
                  :custom-service-list="item.customServiceList"
                  :default-service-list="item.serviceList"
                />
              </template>

              <!-- アクション -->
              <template #cell(actionType)="{ value }">
                {{ $filter.enumTo(value, "allow_deny") }}
              </template>

              <!-- 有効/有無 -->
              <template #cell(isPolicyStatus)="{ value }">
                {{ $filter.enumTo(value, "enable") }}
              </template>

              <!-- 備考 -->
              <template #cell(description)="{ value }">
                {{ $filter.emptyTo(value, "-") }}
              </template>

              <!-- 削除 -->
              <template #cell(delete)="{ item }">
                <v-btn
                  v-if="item.policyCategoryInfo !== 'UNEDITABLE_DEFAULT_POLICY'"
                  density="comfortable"
                  color="primary"
                  @click.stop="deletePolicy(item)"
                  >削除
                </v-btn>
              </template>
            </app-draggable-table>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn variant="flat" color="primary" @click="submit">保存</v-btn>
            <v-btn variant="outlined" color="primary" @click="closeModal"
              >キャンセル
            </v-btn>
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import IntranetPolicySrcAddress from "@/components/policy/IntranetPolicySrcAddress.vue";
import IntranetPolicyDstAddress from "@/components/policy/IntranetPolicyDstAddress.vue";
import IntranetPolicyService from "@/components/policy/IntranetPolicyService.vue";
import type { IntranetFwPolicy, IntraPolicy } from "@/apis/IntranetfwPolicyApi";
import type { UpperLimitEntity } from "@/apis/UpperLimitApi";
import type { Pfgw } from "@/apis/IntranetfwServiceMenuApi";
import type { PfgwZoneType } from "@/modals/applianceContractSetting/IntranetFirewall/policy/pfgw/IntranetFwPfgwPolicyZoneList.vue";
import { VueFinalModal } from "vue-final-modal";
import type { AppDraggableTableHeader } from "@/components/AppDraggableTable.vue";
import IntranetFwPfgwPolicyRuleEdit from "@/modals/applianceContractSetting/IntranetFirewall/policy/pfgw/IntranetFwPfgwPolicyRuleEdit.vue";

export default defineComponent({
  name: "IntranetFwPfgwPolicyRuleViewRD",
  components: {
    IntranetPolicySrcAddress,
    IntranetPolicyDstAddress,
    IntranetPolicyService,
  },
  props: {
    /** 送信元ゾーン */
    srcZone: {
      type: String as PropType<PfgwZoneType>,
      required: true,
    },
    /** ポリシーリスト */
    policyList: {
      type: Array as PropType<IntraPolicy[]>,
      required: true,
    },
    /** イントラネットPFGWポリシー全体 */
    intranetFwPolicy: {
      type: Object as PropType<IntranetFwPolicy>,
      required: true,
    },
    /** PFGW */
    pfgw: {
      type: Object as PropType<Pfgw>,
      required: true,
    },
  },
  data() {
    return {
      intranetPolicyTable: {
        fields: [
          { key: "_handle", title: "↑↓", align: "center", width: "" },
          { key: "policyId", title: "ID", width: "80px" },
          { key: "srcAddress", title: "送信元アドレス", width: "150px" },
          { key: "dstAddress", title: "宛先アドレス", width: "150px" },
          { key: "service", title: "サービス", width: "15%" },
          { key: "actionType", title: "アクション", width: "80px" },
          { key: "isPolicyStatus", title: "有効/無効", width: "70px" },
          { key: "description", title: "備考", width: "200px" },
          { key: "delete", title: "", width: "80px" },
        ] as AppDraggableTableHeader<IntraPolicy>[],
        items: cloneDeep(this.policyList),
      },
      intranetFwPfgwPolicyLimit: [] as UpperLimitEntity[],
      isLoaded: false,
      // 変更検知用の初期データ
      initialPolicyList: [] as IntraPolicy[],
    };
  },
  computed: {
    /** 変更があるかどうかを判定 */
    hasChanges(): boolean {
      return !isEqual(this.intranetPolicyTable.items, this.initialPolicyList);
    },
    /** true: 作成できるポリシー数の上限, false: 上限ではない */
    limitPolicy(): boolean {
      // イントラネットFW側のカスタムポリシー数（ゾーン横断）
      const totalPolicy =
        (this.latestIntranetFwPfgwPolicy.wvs2ToKcpsList
          .flatMap((e) => e.policyList)
          .filter((e) => e.policyCategoryInfo === "CUSTOM_POLICY").length ??
          0) +
        (this.latestIntranetFwPfgwPolicy.kcpsToWvs2List
          .flatMap((e) => e.policyList)
          .filter((e) => e.policyCategoryInfo === "CUSTOM_POLICY").length ?? 0);
      return (
        totalPolicy >=
        this.intranetFwPfgwPolicyLimit!.filter(
          (e) => e.upperLimitItemName === "ポリシー数(PFGW(イントラ))"
        )[0]!.upperLimit
      );
    },
    /** 最新の全体ポリシー（全体ポリシー + 編集中のポリシー ）*/
    latestIntranetFwPfgwPolicy(): IntranetFwPolicy {
      return {
        ...this.intranetFwPolicy,
        ...(this.srcZone === "WVS2"
          ? {
              wvs2ToKcpsList: this.intranetFwPolicy.wvs2ToKcpsList.map((e) => {
                if (e.enumberAct === this.pfgw.enumberAct) {
                  return { ...e, policyList: this.intranetPolicyTable.items };
                }
                return e;
              }),
            }
          : {}),
        ...(this.srcZone === "KCPS"
          ? {
              kcpsToWvs2List: this.intranetFwPolicy.kcpsToWvs2List.map((e) => {
                if (e.enumberAct === this.pfgw.enumberAct) {
                  return { ...e, policyList: this.intranetPolicyTable.items };
                }
                return e;
              }),
            }
          : {}),
      };
    },
  },
  async mounted() {
    // 初期データを保存
    this.initialPolicyList = cloneDeep(this.policyList);
    /** イントラネットPFGWの設定上限値を取得 */
    this.intranetFwPfgwPolicyLimit = (
      await this.$api.upperLimit.getUpperLimitLine({
        upperLimitLineManageUnit: "PFGW",
        enumber: this.pfgw.enumberAct,
      })
    ).pfgwLine!.upperLimitList!;
    this.isLoaded = true;
  },
  methods: {
    tdClass(field: AppDraggableTableHeader<unknown>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
    /** イントラネットFWポリシー追加モーダルの表示 */
    async showIntraPolicyRegister() {
      // ポリシールール追加の表示 追加後のデータを受け取る
      const policy: IntraPolicy = await this.$modal.show(
        IntranetFwPfgwPolicyRuleEdit,
        {
          srcZone: this.srcZone,
          enumberAct: this.pfgw.enumberAct,
          allPolicy: [
            ...this.latestIntranetFwPfgwPolicy.kcpsToWvs2List,
            ...this.latestIntranetFwPfgwPolicy.wvs2ToKcpsList,
          ].flatMap((e) => e.policyList),
        }
      );

      // 編集不可ポリシーの位置を探索する(最上部・最下部にしか存在しない仕様)
      const last = this.intranetPolicyTable.items
        .map((e) => e.policyCategoryInfo)
        .lastIndexOf("UNEDITABLE_DEFAULT_POLICY");

      // 編集不可ポリシーが最下部にあれば、編集不可ポリシーの1つ前にデータを追加 それ以外は最後にデータを追加
      this.intranetPolicyTable.items.splice(
        last === -1 ? this.intranetPolicyTable.items.length : last,
        0,
        policy
      );
    },

    /** イントラネットFWポリシー変更モーダルの表示 */
    async showIntraPolicyModify(row: IntraPolicy) {
      // 編集不可デフォルトポリシーの場合は処理終了
      if (row.policyCategoryInfo === "UNEDITABLE_DEFAULT_POLICY") {
        return;
      }

      // ポリシールール編集の表示 変更後のデータを受け取る
      const policy: IntraPolicy = await this.$modal.show(
        IntranetFwPfgwPolicyRuleEdit,
        {
          srcZone: this.srcZone,
          policy: row,
          enumberAct: this.pfgw.enumberAct,
        }
      );
      // 変更データの反映
      const index = this.intranetPolicyTable.items.indexOf(row);
      this.intranetPolicyTable.items[index] = policy;
    },

    /** ポリシー削除 */
    async deletePolicy(item: IntraPolicy) {
      const index = this.intranetPolicyTable.items.indexOf(item);
      this.intranetPolicyTable.items.splice(index, 1);
    },

    /** ポリシー編集完了 */
    async submit() {
      // OKが押された場合、編集されたポリシーを遷移元に返却する
      (
        this.$refs.modal as typeof VueFinalModal & {
          ok: (e: IntraPolicy[]) => void;
        }
      ).ok(this.intranetPolicyTable.items);
    },
    /** モーダルを閉じる処理（変更がある場合は確認ダイアログを表示） */
    async closeModal() {
      if (!this.hasChanges) {
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
        return;
      }

      try {
        await this.$confirm("変更を破棄してキャンセルします、よろしいですか？");
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
      } catch {
        // キャンセルの場合何もしない（モーダルは開いたまま）
      }
    },

    /** ポリシーテーブルのtrクラス. */
    policyRowClass(
      item: Pick<IntraPolicy, "policyCategoryInfo">
    ): string[] | undefined {
      switch (item.policyCategoryInfo) {
        case "CUSTOM_POLICY":
          // ドラッグ可能
          return ["draggable", "clickable"];
        case "EDITABLE_DEFAULT_POLICY":
          // ドラッグ可能
          return ["draggable", "clickable"];
        case "UNEDITABLE_DEFAULT_POLICY":
          // ドラッグ不可 + 背景色グレーアウト
          return ["gray-out"];
      }
    },
  },
});
</script>

<style lang="scss" scoped>
:deep(th) {
  font-size: 90%;
}
</style>
